import { closeRxDB } from '..'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

const LOGOUT_FLAG_KEY = 'moly_reset_rxdb_pending'

let installed = false

/**
 * Listen for cross-tab logout signals so this tab gracefully shuts down its
 * RxDB instance before another tab calls `indexedDB.deleteDatabase()` on the
 * shared databases.
 *
 * Without this, the other tab's deletion fires `versionchange` on this tab's
 * still-live IDB connections and RxDB Premium's WAL prediction
 * (`runWalBranchPredicted` -> `indexedDBFindByIds`) issues reads on a
 * transaction whose underlying database has been invalidated, throwing
 * `TransactionInactiveError` in the console.
 *
 * The signal is the `moly_reset_rxdb_pending` localStorage flag set by
 * `performLogout()`. The `storage` event only fires in OTHER tabs, so this
 * listener will not run in the tab that initiated the logout.
 */
export const installCrossTabLogoutListener = (): void => {
  if (installed) return
  if (typeof window === 'undefined') return
  installed = true

  window.addEventListener('storage', (event) => {
    if (event.key !== LOGOUT_FLAG_KEY) return
    if (event.newValue !== 'true') return

    logger.info(
      '[RxDB] Cross-tab logout detected — closing local RxDB and reloading',
    )

    // Soft-close (the other tab owns the deletion). Reload regardless so the
    // user lands on the post-logout state with a clean React tree.
    closeRxDB({ remove: false })
      .catch((err) => {
        logger.logError(err as Error, LogCategory.DATABASE_INIT, {
          message: 'Cross-tab RxDB close failed',
        })
      })
      .finally(() => {
        window.location.reload()
      })
  })
}
